import { useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import { createMailContract } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { CardElement, ColorRank } from "@/types/battle"
import { ELEMENT_NAMES, RANK_NAMES } from "@/constants/guardians"

const ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "ICE",
  "WIND",
  "LIGHTNING",
]
const RANKS: ColorRank[] = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

export default function PostOfficeCreateLotPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const pointId = params.get("pointId") ?? ""
  const gps = usePlayerGpsState()
  const { collectionCards, syncGameState, bumpCards } = useGuardiansPassport()

  const [offeredCardId, setOfferedCardId] = useState("")
  const [wantedElement, setWantedElement] = useState<CardElement>("FIRE")
  const [wantedColorRank, setWantedColorRank] = useState<ColorRank>("WHITE")

  const create = useMutation({
    mutationFn: () => {
      if (!gps || !pointId) throw new Error("GPS и точка почты")
      if (!offeredCardId) throw new Error("Выберите карту")
      return createMailContract({
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
        offeredCardId,
        wantedElement,
        wantedColorRank,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      navigate(`/play/post-office?pointId=${encodeURIComponent(pointId)}`)
    },
  })

  return (
    <GameScreenLayout title="Создать лот" backTo="/play/post-office">
      {!pointId ? (
        <p className="text-sm text-red-300">Укажите pointId в URL</p>
      ) : (
        <>
          <p className="mb-3 text-xs text-guardians-muted">Отделение: {pointId}</p>
          <label className="mb-2 block text-xs text-guardians-muted">
            Отдаёте карту
          </label>
          <select
            value={offeredCardId}
            onChange={(e) => setOfferedCardId(e.target.value)}
            className="mb-4 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
          >
            <option value="">—</option>
            {collectionCards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.element} {c.colorRank} Lv.{c.level}
              </option>
            ))}
          </select>
          <label className="mb-2 block text-xs text-guardians-muted">Хотите</label>
          <div className="mb-4 flex gap-2">
            <select
              value={wantedElement}
              onChange={(e) => setWantedElement(e.target.value as CardElement)}
              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-sm"
            >
              {ELEMENTS.map((el) => (
                <option key={el} value={el}>
                  {ELEMENT_NAMES[el]}
                </option>
              ))}
            </select>
            <select
              value={wantedColorRank}
              onChange={(e) => setWantedColorRank(e.target.value as ColorRank)}
              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-sm"
            >
              {RANKS.map((r) => (
                <option key={r} value={r}>
                  {RANK_NAMES[r]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={create.isPending || !gps}
            onClick={() => create.mutate()}
            className="w-full rounded-xl bg-guardians-accent py-3 text-sm text-guardians-bg disabled:opacity-50"
          >
            Создать обмен
          </button>
          {create.isError ? (
            <p className="mt-3 text-sm text-red-300">
              {getGameApiErrorMessage(create.error, "Ошибка")}
            </p>
          ) : null}
        </>
      )}
    </GameScreenLayout>
  )
}
